import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import plugIcon from '../../client_customizations/assets/images/icons/plug-icon.svg';
import boltIcon from '../../client_customizations/assets/images/icons/bolt-icon.svg';
import fastChargeIcon from '../../client_customizations/assets/images/icons/fastcharge-icon.svg';
import fullChargeChart from '../../client_customizations/assets/images/fullcharge-chart.svg';
import './faq.scss';

const FAQ_DATA = [
  {
    q: (
      <FormattedMessage
        id="ae-main-faq1"
        defaultMessage="🚗⚡ What is an electric vehicle (EV)?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq1A"
        defaultMessage={`
<p>An EV is a vehicle that uses electricity as its primary fuel. It can be:</p>
<ul>
  <li><span>A <strong>Battery Electric Vehicle (BEV)</strong> that runs only on electricity, or </span></li>
  <li><span>A <strong>Plug-in Hybrid Electric Vehicle (PHEV)</strong> that uses both electricity and gasoline. </span></li>
</ul>
        `}
        values={{
          p: (chunks) => <p>{chunks}</p>,
          ul: (chunks) => <ul>{chunks}</ul>,
          li: (chunks) => <li>{chunks}</li>,
          span: (chunks) => <span>{chunks}</span>,
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq2"
        defaultMessage="💰 Are EVs more expensive than gas vehicles?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq2A"
        defaultMessage={`
<p>While EVs may cost more upfront, they are cheaper to operate. Lower fuel costs, fewer maintenance needs, and available rebates and tax credits often make EVs more affordable over time.</p>
        `}
        values={{
          p: (chunks) => <p>{chunks}</p>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq3"
        defaultMessage="⚡  What are Level 1, Level 2, and DC Fast Charging?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq3A"
        defaultMessage={`
<columns>
  <column>
    <img1></img1>
    <p><strong>Level 1</strong></p>
    <ul>
      <li><span>Chargers that plug into a regular 120-volt home outlet, just like the one you use to charge your phone or toothbrush.</span></li>
      <li><span>Slow charge rate: 3-5 miles per hour.</span></li>
      <li><span>Useful for drivers traveling 40 miles or less daily.</span></li>
      <li><span>Level 1 charging is best for charging at home overnight.</span></li>
    </ul>
  </column>
  <column>
    <img2></img2>
    <p><strong>Level 2</strong></p>
    <ul>
      <li><span>240-volt chargers, like the one used for a clothes dryer outlet.</span></li>
      <li><span>Medium charge rate: 15-30 miles per hour.</span></li>
      <li><span>Useful for drivers traveling 100+ miles daily.</span></li>
      <li><span>Level 2 charging is best when you have time to charge over several hours.</span></li>
    </ul>
  </column>
  <column>
    <img3></img3>
    <p><strong>DC Fast Charging</strong></p>
    <ul>
      <li><span>Three connector types: CHAdeMO, SAE Combo (CCS), and NACS (Tesla).</span></li>
      <li><span>Fast charge rate: 150-400 miles per hour.</span></li>
      <li><span>Useful for long-distance driving 300+ miles daily.</span></li>
      <li><span>Fast charging is best when you are on the go and need to charge your EV in under 30 minutes.</span></li>
    </ul>
  </column>
</columns>
  `}
        values={{
          columns: (chunks) => <div className="faq-columns">{chunks}</div>,
          column: (chunks) => <div className="faq-column">{chunks}</div>,
          img1: (chunks) => <img src={plugIcon} alt="Level 1 Charging" />,
          img2: (chunks) => <img src={boltIcon} alt="Level 2 Charging" />,
          img3: (chunks) => <img src={fastChargeIcon} alt="DC Fast Charging" />,
          p: (chunks) => <p>{chunks}</p>,
          strong: (chunks) => <strong>{chunks}</strong>,
          ul: (chunks) => <ul>{chunks}</ul>,
          li: (chunks) => <li>{chunks}</li>,
          span: (chunks) => <span>{chunks}</span>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq4"
        defaultMessage="🔋 How far can I drive on a full charge?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq4A"
        defaultMessage={`
<img1></img1>
<p>Most EVs today offer <strong>230-350 miles per charge</strong>, which covers <strong>multiple days of driving</strong> for the average Austin commuter (approx. 25 miles round trip daily).</p>
        `}
        values={{
          img1: () => (
            <img
              src={fullChargeChart}
              alt="Charge Chart"
              className="img-center"
            />
          ),
          p: (chunks) => <p>{chunks}</p>,
          strong: (chunks) => <strong>{chunks}</strong>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq5"
        defaultMessage="🏡 Can I charge my EV at home?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq5A"
        defaultMessage={`
<p>
  Yes! One of the biggest perks of driving an EV is to charge right at home while you sleep. 
  While you can plug an EV into a regular 120-volt outlet, a Level 2 (240-volt) charging station gives you a faster charge. 
  Austin Energy offers a <a1>rebate for a Level 2 home charger</a1>.
</p>

<p>
  When to charge?<break></break>
  All electric vehicles come with timers that allow you to pre-set when the car charges. 
  It is best to charge your electric vehicle between 10 p.m. and 6 a.m. when renewable wind power peaks and there is more capacity on the Texas grid. 
  It is best to avoid charging your electric vehicle between 4 p.m. to 7 p.m. when there is typically less capacity in the Texas grid. 
  <a2>(Texas Department of Transportation)</a2>
</p>

<p>
  ➢ Get bill credits for using your home charger during periods of low energy demand through 
  <a3>Power Partner℠ EV</a3>.
</p>
  `}
        values={{
          p: (chunks) => <p>{chunks}</p>,
          break: () => <br />,
          a1: (chunks) => (
            <a
              href="https://austinenergy.com/green-power/plug-in-austin/home-charging"
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </a>
          ),
          a2: (chunks) => (
            <a
              href="https://www.txdot.gov/discover/ev-charging-stations.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </a>
          ),
          a3: (chunks) => (
            <a
              href="https://austinenergy.com/green-power/plug-in-austin/power-partner-ev"
              target="_blank"
              rel="noopener noreferrer"
            >
              {chunks}
            </a>
          ),
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq6"
        defaultMessage="🏙️ What if I live in an apartment or rent?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq6A"
        defaultMessage={`<p>You can still go electric! <a></a>: Austin Energy offers charging options for EVeryone. No subscription required.</p>`}
        values={{
          p: (chunks) => <p>{chunks}</p>,
          a: () => (
            <a
              href="https://austinenergy.com/green-power/plug-in-austin/everything-charging"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage
                id="ae.chargeOnTheGo"
                defaultMessage="Charge-On-The-Go"
              />
            </a>
          ),
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq7"
        defaultMessage="📍 Where can I find public charging stations?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq7A"
        defaultMessage={`
<p>Austin Energy’s <a>EV charging network</a> includes thousands of public stations, including fast charging. There are also other networks available all throughout Austin!</p>
        `}
        values={{
          p: (chunks) => <p>{chunks}</p>,
          a: (chunks) => <a href="/charging-stations">{chunks}</a>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq8"
        defaultMessage="💰📋 What rebates and incentives are available?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq8A"
        defaultMessage={`<p><a></a></p>`}
        values={{
          p: (chunks) => <p>{chunks}</p>,
          a: () => (
            <a href="/incentives">
              <FormattedMessage
                id="ae.checkCurrentIncentives"
                defaultMessage="Check current incentives"
              />
            </a>
          ),
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq11"
        defaultMessage="🧰  What about maintenance?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq11A"
        defaultMessage={`<p>EVs have fewer moving parts, no oil changes, and less wear on brakes. That means less maintenance and lower lifetime costs. </p>`}
        values={{
          p: (chunks) => <p>{chunks}</p>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq9"
        defaultMessage="🌎  Are EVs really better for the environment?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq9A"
        defaultMessage={`
<p>Yes. EVs produce zero tailpipe emissions and are more efficient overall, even when charged on today's electric grid. They help improve air quality and reduce greenhouse gas emissions.</p>
        `}
        values={{
          p: (chunks) => <p>{chunks}</p>,
        }}
      />
    ),
  },
  {
    q: (
      <FormattedMessage
        id="ae-main-faq10"
        defaultMessage="🚘  How do I choose the right EV for me?"
      />
    ),
    a: (
      <FormattedMessage
        id="ae-main-faq10A"
        defaultMessage={`<p>Use the EV Buyer's Guide to compare models by price, range, features, and more. Whether you drive daily, carpool, or want something fun, there's an EV that fits your life.</p><p><a></a></p>`}
        values={{
          p: (chunks) => <p>{chunks}</p>,
          a: () => (
            <a href="/vehicles">
              <FormattedMessage
                id="ae.findYourEvMatch"
                defaultMessage="Find Your EV Match"
              />
            </a>
          ),
        }}
      />
    ),
  },
];

const Chevron = ({ expanded }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="30"
    height="30"
    viewBox="0 0 24 24"
    style={{
      transition: 'transform 180ms ease',
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    }}
  >
    <path
      d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"
      fill="currentColor"
    />
  </svg>
);

const FAQItem = ({ idBase, index, q, a, expanded, onToggle }) => {
  const buttonId = `${idBase}-faq-button-${index}`;
  const panelId = `${idBase}-faq-panel-${index}`;

  return (
    <div className="faq-item border rounded-3 mb-2">
      <h3 className="m-0">
        <button
          id={buttonId}
          className="faq-trigger w-100 d-flex align-items-center justify-content-between text-start p-3 border-0 bg-white"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="pe-3">{q}</span>
          <Chevron expanded={expanded} />
        </button>
      </h3>

      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!expanded}
        className="faq-panel px-3 pb-3"
      >
        <p className="mb-0">{a}</p>
      </div>
    </div>
  );
};

const FAQ = ({ ip, uuid, language }) => {
  const [open, setOpen] = useState(() => new Set());
  const idBase = useRef(
    `faq-${Math.random().toString(36).slice(2, 9)}`
  ).current;

  const toggle = (i) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });

  useEffect(() => {
    document.title = `${process.env.REACT_APP_PAGES_FAQ_TITLE} - Austin Energy EV Buyers Guide`;
  });

  return (
    <section
      className="faq-container"
      aria-label="Frequently Asked Questions Page"
    >
      <div className="container">
        <div className="faq-main-title">
          <h1>
            <FormattedMessage id="faqMainTitle" />
          </h1>
          <p>
            <FormattedMessage id="faqMainDescription" />
          </p>
        </div>

        <div className="faq-list">
          {FAQ_DATA.map((item, i) => (
            <FAQItem
              key={i}
              idBase={idBase}
              index={i}
              q={item.q}
              a={item.a}
              expanded={open.has(i)}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>

        <style>{`
        .faq-item:hover .faq-trigger { background-color: #f8f9fa; }
        .faq-trigger { font-size: 1rem; }
        .faq-panel { background-color: #fff; }
      `}</style>
      </div>
    </section>
  );
};

export default FAQ;

FAQ.propTypes = {
  ip: PropTypes.string,
  uuid: PropTypes.string,
};
